/**
 * spiceHold: how a SPICE plan's state reads, from its stored columns.
 *
 * A plan is regular (active), covered (another therapist takes its visits)
 * or paused (on_hold, no visits). An away plan, covered or paused, carries
 * a reason: vacation, medical or other. Rows written by older code carry
 * the status words 'vacation' and 'hospital' for a pause, and an away row
 * may carry no reason at all. effectiveHold maps every stored shape to the
 * three-part one every reader uses:
 *
 *   covered, with a cover  covered / its reason, or other when none / the cover
 *   covered, no cover      on_hold / its reason, or other when none
 *   vacation               on_hold / vacation
 *   hospital               on_hold / medical
 *   on_hold                on_hold / its reason, or other when none
 *   active                 active  / null, whatever hold_reason says
 *
 * The cover is read only on a covered row; on any other row it reads null.
 * The caller hands in the cover as its loader found it: a cover whose
 * roster row is gone, inactive or no longer does SPICE is handed in as
 * null, so the plan reads paused.
 *
 * An away plan with an until date ends on its own: on the until day itself
 * it is still away, and from the next day it reads regular. The stored row
 * is not rewritten; the date is compared at read time.
 *
 * legacyStatusWord goes the other way, for the wires that carry one word:
 * covered reads 'covered', on_hold/vacation reads 'vacation',
 * on_hold/medical reads 'hospital', any other pause reads 'on_hold'.
 *
 * Mirror of api/_shared/spiceHold.ts (no bridge between the api/ and src/
 * packages); keep the two in lockstep.
 */

import type { SpiceHoldReason, SpiceVisitPlanStatus } from './spiceVisitPlansApi';

export type SpiceEffectiveStatus = 'active' | 'covered' | 'on_hold';

export interface SpiceEffectiveHold {
  status: SpiceEffectiveStatus;
  hold_reason: SpiceHoldReason | null;
  cover_roster_id: string | null;
}

/** The plan's cover as its loader read it (null when there is none that can cover). */
export interface SpiceCoverFact {
  coverRosterId: string | null;
}

const ACTIVE: SpiceEffectiveHold = { status: 'active', hold_reason: null, cover_roster_id: null };

/**
 * The plan's state as it reads on `todayIso` ('YYYY-MM-DD'). A week reader
 * passes each entry's own date as `todayIso`, so a cover or a pause ending
 * midweek reads away on its earlier days and regular on its later ones.
 */
export function effectiveHold(
  status: string,
  holdReason: string | null,
  statusUntil: string | null,
  cover: SpiceCoverFact,
  todayIso: string,
): SpiceEffectiveHold {
  let reason: SpiceHoldReason;
  if (status === 'vacation') reason = 'vacation';
  else if (status === 'hospital') reason = 'medical';
  else if (status === 'on_hold' || status === 'covered') reason = isHoldReason(holdReason) ? holdReason : 'other';
  else return { ...ACTIVE };

  if (statusUntil !== null && statusUntil < todayIso) return { ...ACTIVE };
  if (status === 'covered' && cover.coverRosterId !== null) {
    return { status: 'covered', hold_reason: reason, cover_roster_id: cover.coverRosterId };
  }
  return { status: 'on_hold', hold_reason: reason, cover_roster_id: null };
}

/** The status word an older bundle reads for a pause with this reason. */
export function legacyHoldWord(reason: SpiceHoldReason | null): Exclude<SpiceVisitPlanStatus, 'active' | 'covered'> {
  if (reason === 'vacation') return 'vacation';
  if (reason === 'medical') return 'hospital';
  return 'on_hold';
}

/** The one status word a wire carries for this state. */
export function legacyStatusWord(hold: SpiceEffectiveHold): SpiceVisitPlanStatus {
  if (hold.status === 'active') return 'active';
  if (hold.status === 'covered') return 'covered';
  return legacyHoldWord(hold.hold_reason);
}

export function isHoldReason(value: unknown): value is SpiceHoldReason {
  return value === 'vacation' || value === 'medical' || value === 'other';
}
